import React, { useState, useCallback, useContext, createContext, useMemo } from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// Context
// ============================================================================

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error('Tabs compound components must be used within a <Tabs> provider.');
  }
  return ctx;
}

// ============================================================================
// Tabs (root)
// ============================================================================

export interface TabsProps<T extends string = string> {
  /** Controlled active tab value. */
  value?: T;
  /** Callback when the active tab changes. */
  onValueChange?: (value: T) => void;
  /** Default active tab (uncontrolled mode). */
  defaultValue?: T;
  children: React.ReactNode;
  className?: string;
}

export function Tabs<T extends string = string>({
  value,
  onValueChange,
  defaultValue,
  children,
  className,
}: Readonly<TabsProps<T>>) {
  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');

  const isControlled = value !== undefined;
  const activeTab = isControlled ? value : internalValue;

  const setActiveTab = useCallback(
    (next: string) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      // `next` originates from the string-typed TabTrigger values; the public
      // callback is narrowed to the caller's tab union via the T generic.
      onValueChange?.(next as T);
    },
    [isControlled, onValueChange]
  );

  const ctx = useMemo(() => ({ activeTab, setActiveTab }), [activeTab, setActiveTab]);

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

// ============================================================================
// TabList
// ============================================================================

export interface TabListProps {
  children: React.ReactNode;
  className?: string;
}

export function TabList({ children, className }: Readonly<TabListProps>) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex border-b border-border gap-1',
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TabTrigger
// ============================================================================

export interface TabTriggerProps {
  /** Value that identifies this tab. Must match the corresponding TabContent value. */
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  activeClassName?: string;
  inactiveClassName?: string;
}

export function TabTrigger({
  value,
  children,
  className,
  disabled = false,
  activeClassName,
  inactiveClassName,
}: Readonly<TabTriggerProps>) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  const defaultActiveClassName = 'border-primary text-primary';
  const defaultInactiveClassName = 'border-transparent text-muted-foreground hover:text-foreground hover:border-border';

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={`tabpanel-${value}`}
      id={`tab-${value}`}
      disabled={disabled}
      onClick={() => {
        if (!disabled) setActiveTab(value);
      }}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
        'border-b-2 -mb-px',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive ? (activeClassName ?? defaultActiveClassName) : (inactiveClassName ?? defaultInactiveClassName),
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      {children}
    </button>
  );
}

// ============================================================================
// TabContent
// ============================================================================

export interface TabContentProps {
  /** Value that identifies this panel. Must match the corresponding TabTrigger value. */
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabContent({ value, children, className }: Readonly<TabContentProps>) {
  const { activeTab } = useTabsContext();

  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      className={cn('pt-4', className)}
    >
      {children}
    </div>
  );
}
